import { Head } from "@inertiajs/react"
import { useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

import EntryForm, { type EntryFormData, type EvidenceTypeOption } from "./_form"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface ReportRef {
  id: number
}

interface EntryData {
  entry_type: number
  start_time: string
  entry_text: string
  evidence_type_ids: number[]
  significant: number
}

interface Props {
  case_record: CaseRecord
  report: ReportRef
  entry: EntryData
  evidence_types: EvidenceTypeOption[]
  errors?: Record<string, string[]>
}

export default function New({
  case_record,
  report,
  entry,
  evidence_types,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Reports", href: `/cases/${case_record.id}/reports` },
    {
      title: `Report #${report.id}`,
      href: `/cases/${case_record.id}/reports/${report.id}`,
    },
    {
      title: "New Entry",
      href: `/cases/${case_record.id}/reports/${report.id}/entries/new`,
    },
  ]

  const { data, setData, post, processing, transform } = useForm<EntryFormData>(
    {
      entry_type: entry.entry_type,
      start_time: entry.start_time,
      entry_text: entry.entry_text,
      evidence_type_ids: entry.evidence_type_ids ?? [],
      significant: entry.significant,
    },
  )

  transform((data) => ({ case_report_entry: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/cases/${case_record.id}/reports/${report.id}/entries`)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`New Entry - ${case_record.case_number}`} />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">New Report Entry</h1>
        <EntryForm
          caseId={case_record.id}
          reportId={report.id}
          data={data}
          setData={setData}
          errors={errors}
          processing={processing}
          onSubmit={handleSubmit}
          submitLabel="Create Entry"
          evidenceTypes={evidence_types}
        />
      </div>
    </AppLayout>
  )
}
